import { downloadInvoicePdf, sendEInvoice, type InvoiceTemplate } from '@/api/invoices';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import {
  useDunningEscalate,
  useDunningHistory,
  useDunningStatus,
  useInvoice,
  useInvoiceMutations,
} from '@/hooks/use-invoices';
import { useTranslation } from '@kgc/i18n';
import { toast } from '@kgc/ui';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StornoModal } from './components/StornoModal';
import { navPartnerErrorKey } from './e-invoice-error';
import {
  DUNNING_LEVELS,
  INVOICE_STATUSES,
  INVOICE_TYPES,
  NAV_STATUSES,
  PAYMENT_METHODS,
} from './mock-data';
import type { Invoice, InvoiceStatus, InvoiceType, PaymentMethod } from './types';

export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { invoice, isLoading, error, refetch } = useInvoice(id);
  const mutations = useInvoiceMutations();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">{t('invoice.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('invoice.notFound')}
          </h1>
          {error && <p className="mt-2 text-red-600 dark:text-red-400">{error}</p>}
          <Button onClick={() => navigate('/invoices')} className="mt-4">
            {t('common.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <InvoiceDetail
      invoice={invoice}
      navigate={navigate}
      mutations={mutations}
      refetch={refetch}
      invoiceId={id}
    />
  );
}

function InvoiceDetail({
  invoice,
  navigate,
  mutations,
  refetch,
  invoiceId,
}: {
  invoice: Invoice;
  navigate: ReturnType<typeof useNavigate>;
  mutations: ReturnType<typeof useInvoiceMutations>;
  refetch: () => Promise<void>;
  invoiceId: string | undefined;
}) {
  const { t } = useTranslation();

  // Dunning data (FIN-FG-4-5)
  const { invoices: dunningInvoices } = useDunningStatus();
  const { history: dunningHistory, refetch: refetchHistory } = useDunningHistory(invoiceId);
  const { escalate: dunningEscalate, isLoading: dunningEscalating } = useDunningEscalate();

  const currentDunningLevel = useMemo(() => {
    const found = dunningInvoices.find(d => d.id === invoice.id);
    return found?.dunningLevel ?? 'NONE';
  }, [dunningInvoices, invoice.id]);

  const currentDunningInvoice = useMemo(() => {
    return dunningInvoices.find(d => d.id === invoice.id);
  }, [dunningInvoices, invoice.id]);

  const getDunningLevelBadge = (level: string) => {
    const config = DUNNING_LEVELS.find(d => d.value === level);
    if (!config) return null;
    return (
      <span className={`rounded-full px-3 py-1 text-sm font-medium ${config.color}`}>
        {t(config.labelKey)}
      </span>
    );
  };

  const handleDunningEscalate = async () => {
    try {
      await dunningEscalate(invoice.id);
      await refetchHistory();
      alert(t('dunning.escalateSuccess'));
    } catch (err) {
      alert(
        t('invoice.errorOccurred', {
          message: err instanceof Error ? err.message : t('invoice.unknownError'),
        })
      );
    }
  };
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('hu-HU');
  };

  const getStatusBadge = (status: InvoiceStatus) => {
    const config = INVOICE_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-3 py-1 text-sm font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : status}
      </span>
    );
  };

  const getTypeBadge = (type: InvoiceType) => {
    const config = INVOICE_TYPES.find(tp => tp.value === type);
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : type}
      </span>
    );
  };

  const getPaymentMethodLabel = (method?: PaymentMethod) => {
    if (!method) return '-';
    const config = PAYMENT_METHODS.find(m => m.value === method);
    return config ? t(config.labelKey) : method;
  };

  const getNavStatusBadge = (navStatus?: string) => {
    if (!navStatus) return <span className="text-muted-foreground">-</span>;
    const config = NAV_STATUSES.find(s => s.value === navStatus.toUpperCase());
    return (
      <span className={`rounded-full px-3 py-1 text-sm font-medium ${config?.color ?? ''}`}>
        {config ? t(config.labelKey) : navStatus}
      </span>
    );
  };

  const [eInvoiceSending, setEInvoiceSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate>('STANDARD');
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [stornoModalOpen, setStornoModalOpen] = useState(false);

  const templateOptions: Array<{ value: InvoiceTemplate; label: string }> = [
    { value: 'STANDARD', label: t('invoice.template.standard') },
    { value: 'DETAILED', label: t('invoice.template.detailed') },
    { value: 'PROFORMA', label: t('invoice.template.proforma') },
  ];

  const handlePrintPDF = async () => {
    setPdfDownloading(true);
    try {
      const blob = await downloadInvoicePdf(invoice.id, { template: selectedTemplate });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `szamla_${invoice.invoiceNumber.replace(/[/\\]/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(
        t('invoice.errorOccurred', {
          message: err instanceof Error ? err.message : t('invoice.unknownError'),
        })
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleSendEmail = () => {
    alert(t('invoice.emailAlert', { number: invoice.invoiceNumber }));
  };

  const handleSendEInvoice = async () => {
    const email = invoice.partner?.eInvoiceEmail;
    if (!email) {
      alert(t('invoice.eInvoice.noEmail'));
      return;
    }
    setEInvoiceSending(true);
    try {
      const result = await sendEInvoice(invoice.id, email);
      await refetch();
      alert(
        `${t('invoice.eInvoice.sent')}\n${t('invoice.eInvoice.hash')}: ${result.data.hash.substring(0, 16)}...`
      );
    } catch (err) {
      // #607: NAV-partner vevő-adat-hiba (NAV_PARTNER_NOT_FOUND / NAV_PARTNER_INCOMPLETE) →
      // user-facing warning-toast (megelőzi az érvénytelen NAV-számlát); minden más hiba marad
      // a meglévő generikus alert-en (minimális blast-radius).
      const navPartnerKey = navPartnerErrorKey(err);
      if (navPartnerKey) {
        // #607 22-kör: action-link a partner-edit-re → az eladó egy kattintással javíthatja
        // a hiányzó NAV-vevő-adatot (név/cím/adószám), majd újraküldheti a számlát.
        toast.warning(t(navPartnerKey), {
          action: {
            label: t('invoice.eInvoice.fixPartner'),
            onClick: () => navigate(`/partners/${invoice.partnerId}/edit`),
          },
        });
      } else {
        alert(
          t('invoice.errorOccurred', {
            message: err instanceof Error ? err.message : t('invoice.unknownError'),
          })
        );
      }
    } finally {
      setEInvoiceSending(false);
    }
  };

  const handleResendToNAV = () => {
    alert(t('invoice.navResendAlert', { number: invoice.invoiceNumber }));
  };

  const handleMarkPaid = async () => {
    try {
      await mutations.recordPayment(invoice.id, {
        amount: Number(invoice.grossAmount) - Number(invoice.paidAmount),
      });
      await refetch();
      alert(t('invoice.markPaidAlert', { number: invoice.invoiceNumber }));
    } catch (err) {
      alert(
        t('invoice.errorOccurred', {
          message: err instanceof Error ? err.message : t('invoice.unknownError'),
        })
      );
    }
  };

  const handleCreateCorrection = () => {
    navigate(`/invoices/correction/${invoice.id}`);
  };

  const handleCreateStorno = () => {
    setStornoModalOpen(true);
  };

  const isOverdue = invoice.status === 'OVERDUE';
  const daysOverdue = useMemo(() => {
    if (!isOverdue) return 0;
    const now = new Date();
    const dueDate = new Date(invoice.dueDate);
    return Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
  }, [isOverdue, invoice.dueDate]);

  const canMarkPaid =
    invoice.status === 'SENT' ||
    invoice.status === 'OVERDUE' ||
    invoice.status === 'PARTIALLY_PAID';
  const canCorrect =
    invoice.status !== 'DRAFT' &&
    invoice.status !== 'CANCELLED' &&
    invoice.type !== 'CREDIT_NOTE' &&
    invoice.type !== 'CORRECTION' &&
    invoice.type !== 'PROFORMA';

  // Calculate totals from items if available
  const netTotal = Number(invoice.netAmount);
  const vatTotal = Number(invoice.vatAmount);
  const grossTotal = Number(invoice.grossAmount);
  const balanceDue = Number(invoice.grossAmount) - Number(invoice.paidAmount);
  const paidAmount = Number(invoice.paidAmount);

  // Partner info
  const partnerName = invoice.partner?.name ?? '-';
  const partnerAddress = invoice.partner?.address ?? '-';
  const partnerTaxNumber = invoice.partner?.taxNumber;

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/invoices')}>
                &larr; {t('common.back')}
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {invoice.invoiceNumber}
                  </h1>
                  {getTypeBadge(invoice.type)}
                  {getStatusBadge(invoice.status)}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{partnerName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={selectedTemplate}
                onChange={e => setSelectedTemplate(e.target.value as InvoiceTemplate)}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
              >
                {templateOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <Button variant="outline" onClick={handlePrintPDF} disabled={pdfDownloading}>
                {pdfDownloading ? '...' : 'PDF'}
              </Button>
              <Button variant="outline" onClick={handleSendEmail}>
                Email
              </Button>
              {invoice.partner?.eInvoiceEnabled && (
                <Button
                  variant="outline"
                  className="border-blue-400 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/30"
                  onClick={handleSendEInvoice}
                  disabled={eInvoiceSending}
                >
                  {eInvoiceSending ? '...' : t('invoice.eInvoice.send')}
                </Button>
              )}
              {canMarkPaid && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleMarkPaid}
                  disabled={mutations.isLoading}
                >
                  {t('invoice.paid')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Overdue warning */}
      {isOverdue && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-4">
          <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/30">
            <div className="flex items-center gap-3">
              <span className="text-2xl">!</span>
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">
                  {t('invoice.overdueWarning', { days: daysOverdue })}
                </p>
                <p className="text-sm text-red-600 dark:text-red-300">
                  {t('invoice.overdueBalance', { amount: formatPrice(balanceDue) })}
                </p>
              </div>
              <Button size="sm" className="ml-auto bg-red-600 hover:bg-red-700">
                {t('invoice.paymentReminder')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Partner info */}
            <Card>
              <CardHeader>
                <CardTitle>{t('invoice.buyerData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('invoice.nameLabel')}
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">{partnerName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('invoice.addressLabel')}
                    </label>
                    <p className="text-gray-900 dark:text-gray-100">{partnerAddress}</p>
                  </div>
                  {partnerTaxNumber && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('invoice.taxNumber')}
                      </label>
                      <p className="font-mono text-gray-900 dark:text-gray-100">
                        {partnerTaxNumber}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Items */}
            <Card>
              <CardHeader>
                <CardTitle>{t('invoice.items')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b bg-gray-50 dark:bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('invoice.description')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('invoice.quantity')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('invoice.unitPrice')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('invoice.vat')}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('invoice.total')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-slate-700">
                      {invoice.items?.map(item => (
                        <tr key={item.id}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {item.description}
                            </p>
                            {item.productId && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {t('invoice.productIdLabel', { id: item.productId })}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                            {item.quantity} {item.unit}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                            {formatPrice(Number(item.unitPriceNet))}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                            {item.vatPercentage}%
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                            {formatPrice(Number(item.grossAmount))}
                          </td>
                        </tr>
                      ))}
                      {(!invoice.items || invoice.items.length === 0) && (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                          >
                            {t('invoice.noItemsEmpty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="border-t-2 bg-gray-50 dark:bg-slate-700/50 dark:border-slate-600">
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-2 text-right text-gray-600 dark:text-gray-400"
                        >
                          {t('invoice.netTotalLabel')}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                          {formatPrice(netTotal)}
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-2 text-right text-gray-600 dark:text-gray-400"
                        >
                          {t('invoice.vatTotalLabel')}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                          {formatPrice(vatTotal)}
                        </td>
                      </tr>
                      <tr className="text-lg">
                        <td
                          colSpan={4}
                          className="px-4 py-3 text-right font-bold text-gray-900 dark:text-gray-100"
                        >
                          {t('invoice.payable')}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900 dark:text-gray-100">
                          {formatPrice(grossTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {invoice.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('common.notes')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                    <p className="text-blue-800 dark:text-blue-200">{invoice.notes}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('invoice.invoiceData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('invoice.statusLabel')}
                    </span>
                    {getStatusBadge(invoice.status)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('invoice.typeLabel')}
                    </span>
                    {getTypeBadge(invoice.type)}
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('invoice.issueDateLabel')}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {formatDate(invoice.invoiceDate)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('invoice.dueDateLabel')}
                    </span>
                    <span
                      className={
                        isOverdue
                          ? 'text-red-600 font-medium dark:text-red-400'
                          : 'text-gray-900 dark:text-gray-100'
                      }
                    >
                      {formatDate(invoice.dueDate)}
                    </span>
                  </div>
                  {invoice.paidAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('invoice.paidAt')}
                      </span>
                      <span className="text-green-600 dark:text-green-400">
                        {formatDateTime(invoice.paidAt)}
                      </span>
                    </div>
                  )}
                  <div className="border-t pt-4 dark:border-slate-700">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('invoice.paymentMethod')}
                      </span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {getPaymentMethodLabel(invoice.paymentMethod)}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Correction info (E-56, P-INV-1) */}
            {invoice.type === 'CORRECTION' && (
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
                <CardHeader>
                  <CardTitle>{t('invoice.correctionInvoice')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {invoice.referencedInvoiceNumber && (
                      <div className="flex justify-between">
                        <span className="text-amber-700 dark:text-amber-300">
                          {t('invoice.originalInvoice')}
                        </span>
                        <span className="font-mono text-sm text-amber-900 dark:text-amber-100">
                          {invoice.referencedInvoiceNumber}
                        </span>
                      </div>
                    )}
                    {invoice.modificationIndex !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-amber-700 dark:text-amber-300">
                          {t('invoice.modificationIndex')}
                        </span>
                        <span className="text-amber-900 dark:text-amber-100">
                          {invoice.modificationIndex}.
                        </span>
                      </div>
                    )}
                    {/* P-INV-1: modificationReference for NAV */}
                    {invoice.referencedInvoiceNumber && invoice.modificationIndex !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-amber-700 dark:text-amber-300">
                          {t('invoice.correction.modificationReference')}
                        </span>
                        <span className="font-mono text-xs text-amber-900 dark:text-amber-100">
                          {invoice.referencedInvoiceNumber}/{invoice.modificationIndex}
                        </span>
                      </div>
                    )}
                    {invoice.modificationReason && (
                      <div>
                        <span className="text-sm text-amber-700 dark:text-amber-300">
                          {t('invoice.modificationReason')}
                        </span>
                        <p className="mt-1 text-sm text-amber-900 dark:text-amber-100">
                          {invoice.modificationReason}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* NAV status */}
            <Card>
              <CardHeader>
                <CardTitle>{t('invoice.navOnline')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('invoice.statusLabel')}
                    </span>
                    {getNavStatusBadge(invoice.navStatus)}
                  </div>
                  {invoice.navTransactionId && (
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('invoice.navTransactionId')}
                      </span>
                      <span className="font-mono text-xs text-gray-900 dark:text-gray-100">
                        {invoice.navTransactionId}
                      </span>
                    </div>
                  )}
                  {invoice.navStatus === 'REJECTED' && (
                    <Button variant="outline" className="w-full" onClick={handleResendToNAV}>
                      {t('invoice.resendToNav')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Dunning status (FIN-FG-4-5) */}
            {(isOverdue || currentDunningLevel !== 'NONE') && (
              <Card
                className={
                  currentDunningLevel === 'FINAL_NOTICE'
                    ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                    : currentDunningLevel === 'WARNING'
                      ? 'border-orange-300 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20'
                      : 'border-yellow-300 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
                }
              >
                <CardHeader>
                  <CardTitle>{t('dunning.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        {t('dunning.currentLevel')}
                      </span>
                      {getDunningLevelBadge(currentDunningLevel)}
                    </div>
                    {currentDunningInvoice && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          {t('dunning.overdueDays')}
                        </span>
                        <span className="text-sm font-medium text-foreground">
                          {currentDunningInvoice.overdueDays} {t('dunning.days')}
                        </span>
                      </div>
                    )}
                    {currentDunningInvoice && currentDunningInvoice.escalationCount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">
                          {t('dunning.escalationCount')}
                        </span>
                        <span className="text-sm font-medium text-foreground">
                          {currentDunningInvoice.escalationCount}x
                        </span>
                      </div>
                    )}
                    {currentDunningLevel !== 'FINAL_NOTICE' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={handleDunningEscalate}
                        disabled={dunningEscalating}
                      >
                        {dunningEscalating ? '...' : t('dunning.escalate')}
                      </Button>
                    )}

                    {/* Dunning history timeline */}
                    {dunningHistory.length > 0 && (
                      <div className="mt-3 border-t pt-3 dark:border-slate-700">
                        <p className="text-xs font-medium text-muted-foreground mb-2">
                          {t('dunning.history')}
                        </p>
                        <div className="space-y-2">
                          {dunningHistory.map(entry => (
                            <div key={entry.id} className="flex items-start gap-2 text-xs">
                              <div className="mt-1 h-2 w-2 rounded-full bg-current shrink-0" />
                              <div>
                                <p className="text-foreground font-medium">{entry.level}</p>
                                <p className="text-muted-foreground">
                                  {new Date(entry.createdAt).toLocaleString('hu-HU')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* E-számla status (P-INV-2, ADR-063) */}
            {invoice.partner?.eInvoiceEnabled && (
              <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
                <CardHeader>
                  <CardTitle className="text-blue-700 dark:text-blue-300">
                    {t('invoice.eInvoice.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {invoice.eInvoiceHash && (
                      <div>
                        <span className="text-sm text-blue-600 dark:text-blue-400">
                          {t('invoice.eInvoice.hash')}
                        </span>
                        <p className="font-mono text-xs text-blue-800 dark:text-blue-200 break-all">
                          {invoice.eInvoiceHash}
                        </p>
                      </div>
                    )}
                    {invoice.eInvoiceSentAt && (
                      <div className="flex justify-between">
                        <span className="text-sm text-blue-600 dark:text-blue-400">
                          {t('invoice.eInvoice.sentAt')}
                        </span>
                        <span className="text-sm text-blue-800 dark:text-blue-200">
                          {formatDateTime(invoice.eInvoiceSentAt)}
                        </span>
                      </div>
                    )}
                    {!invoice.eInvoiceSentAt && (
                      <p className="text-sm text-blue-500 dark:text-blue-400">
                        {t('invoice.eInvoice.notSent')}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment status */}
            <Card
              className={
                balanceDue === 0
                  ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                  : 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20'
              }
            >
              <CardContent className="pt-4">
                <div className="text-center">
                  <p
                    className={`text-sm ${balanceDue === 0 ? 'text-green-600 dark:text-green-300' : 'text-orange-600 dark:text-orange-300'}`}
                  >
                    {balanceDue === 0 ? t('invoice.fullyPaid') : t('invoice.balanceDue')}
                  </p>
                  <p
                    className={`text-3xl font-bold ${balanceDue === 0 ? 'text-green-700 dark:text-green-200' : 'text-orange-700 dark:text-orange-200'}`}
                  >
                    {balanceDue === 0 ? 'OK' : formatPrice(balanceDue)}
                  </p>
                  {paidAmount > 0 && balanceDue > 0 && (
                    <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">
                      {t('invoice.paidSoFar', { amount: formatPrice(paidAmount) })}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Total */}
            <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
              <CardContent className="pt-4">
                <div className="text-center">
                  <p className="text-sm text-blue-600 dark:text-blue-300">
                    {t('invoice.invoiceTotal')}
                  </p>
                  <p
                    className={`text-3xl font-bold ${grossTotal < 0 ? 'text-red-700 dark:text-red-200' : 'text-blue-700 dark:text-blue-200'}`}
                  >
                    {formatPrice(grossTotal)}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle>{t('common.actions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <select
                      value={selectedTemplate}
                      onChange={e => setSelectedTemplate(e.target.value as InvoiceTemplate)}
                      className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                    >
                      {templateOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <Button variant="outline" onClick={handlePrintPDF} disabled={pdfDownloading}>
                      {pdfDownloading ? '...' : t('invoice.pdfDownload')}
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleSendEmail}
                  >
                    {t('invoice.sendEmail')}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => window.print()}
                  >
                    {t('invoice.printInvoice')}
                  </Button>
                  {canCorrect && (
                    <>
                      <hr className="my-2 dark:border-slate-700" />
                      <Button
                        variant="outline"
                        className="w-full justify-start text-amber-600 hover:text-amber-700 dark:text-amber-400"
                        onClick={handleCreateCorrection}
                      >
                        {t('invoice.correctionInvoice')}
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-red-600 hover:text-red-700 dark:text-red-400"
                        onClick={handleCreateStorno}
                      >
                        {t('invoice.stornoInvoice')}
                      </Button>
                    </>
                  )}
                  {canMarkPaid && (
                    <>
                      <hr className="my-2 dark:border-slate-700" />
                      <Button
                        className="w-full bg-green-600 hover:bg-green-700"
                        onClick={handleMarkPaid}
                        disabled={mutations.isLoading}
                      >
                        {t('invoice.markPaid')}
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      {stornoModalOpen && (
        <StornoModal
          invoice={invoice}
          onClose={() => setStornoModalOpen(false)}
          onStornoed={() => void refetch()}
        />
      )}
    </div>
  );
}
